import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import {
  Alert,
  Box,
  Button,
  Field,
  HStack,
  Icon,
  Input,
  InputGroup,
  Span,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'
import { CancelIcon } from '@editor/assets/icons'
import { CheckCircleIcon } from '@editor/chakra-ui-icons'
import { EDITOR_TOOLS, MODAL_TYPES, PanelType } from '@editor/config/constants'
import { INFO_PANEL_PADDING } from '@editor/config/styles'
import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import CollapsePanel from '@editor/pages/projects/editor/infoPanels/components/CollapsePanel'
import { toggleCollapsedPanel } from '@editor/pages/projects/editor/store/editor'
import { upsertInspectionItems } from '@editor/pages/projects/editor/store/inspectionItems'
import { deleteInspectionItems } from '@editor/pages/projects/editor/store/thunks/inspectionItems'
import { COMMON_MESSAGE } from '@editor/services/ErrorHandler'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { Tooltip } from '@infraloop/common'
import * as Sentry from '@sentry/react'
import { isTablet } from 'react-device-detect'
import { IoInformationCircleOutline } from 'react-icons/io5'

import { usePolylineSelector } from '../../store/selector'
import { DetectionKind } from '../interfaces'
import { PolylineToolMode, setMode } from '../store/core'
import { cancelWorking, initWorking, resetCurrent, restoreBackupExecution } from '../store/crackDetection'
import {
  resetEditedPolylines,
  setInToolLabelVisibility,
  setWorkingWidth,
  updateCompletedPolylineWidths,
  upsertEditedPolylines,
} from '../store/polyline'
import {
  selectCrackDetectionDirtyState,
  selectCrackDetectionLoadingState,
  selectDetectionKind,
  selectHasDetectionResult,
  selectPointForTargetColor,
  selectTargetColorHex,
} from '../store/selectors'
import { addPolylineWidths, updatePolylines } from '../store/thunks'

/**
 * The 自動検出 buttons, in panel order. Both start the same polygon-drawing flow; the kind they
 * stamp on the run is what makes the rest of it differ.
 */
const DETECTION_BUTTONS = [
  { kind: DetectionKind.Crack, label: 'クラックを検出', loadingText: 'クラック検出中...' },
  { kind: DetectionKind.Chalk, label: 'チョーキングを検出', loadingText: 'チョーキング検出中...' },
]

const ToolSettings = () => {
  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showModal, showErrorModal } = useGlobalModalContext()

  // Store
  const dispatch = useAppDispatch()
  const userProfile = useAppSelector((state) => state.user.userProfile)
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const inspectionSheet = useAppSelector((state) => state.inspectionItems.inspectionSheet)
  const inspectionItems = useAppSelector((state) => state.inspectionItems.items)
  const selectedElementIds = useAppSelector((state) => state.toolUtil.core.selectedElementIds)
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const collapsedPanel = useAppSelector((state) => state.editor.collapsedPanel)
  const isMeshPresent = useAppSelector((state) => state.solidMesh.isPresent)

  const mode = usePolylineSelector((state) => state.core.mode)
  const isSaving = usePolylineSelector((state) => state.core.isSaving)
  const completedPolylines = usePolylineSelector((state) => state.polyline.completedPolylines)
  const editedPolylines = usePolylineSelector((state) => state.polyline.editedPolylines)
  const updatingPolylines = usePolylineSelector((state) => state.polyline.updatingPolylines)
  const polylineWorkingPoints = usePolylineSelector((state) => state.polyline.workingPoints)
  const workingWidth = usePolylineSelector((state) => state.polyline.workingWidth)
  const hasDetectionResult = usePolylineSelector(selectHasDetectionResult)
  const isDetectingCrack = usePolylineSelector(selectCrackDetectionLoadingState)
  const isCrackDetectionDirty = usePolylineSelector(selectCrackDetectionDirtyState)
  const detectionKind = usePolylineSelector(selectDetectionKind)
  const pointForTargetColor = usePolylineSelector(selectPointForTargetColor)
  const targetColorHex = usePolylineSelector(selectTargetColorHex)
  const inToolLabelVisibility = usePolylineSelector((state) => state.polyline.inToolLabelVisibility)

  // Flags
  const isCollapsed = collapsedPanel.includes(PanelType.Tool)
  const isSelectedTool = selectedTool === EDITOR_TOOLS.POLYLINE
  const isPolylineMode = mode === PolylineToolMode.Draw
  const isSavingDetectedPolyline = isSaving && hasDetectionResult
  const isChalkDetection = detectionKind === DetectionKind.Chalk

  /** チョーキング検出 and クラック検出 share this panel, so the noun is all that differs. */
  const detectionLabel = isChalkDetection ? 'チョーキング検出' : 'クラック検出'

  // Chalk width is never measured for us, so hand entry is the only way it is ever set.
  const isWidthLocked = isCrackDetectionDirty && !isChalkDetection

  // Refs
  const inputRef = useRef<HTMLInputElement>(null)

  /**
   * Find all selected polyline inspection items
   */
  const allItems = useMemo(
    () =>
      selectedElementIds.reduce<InspectionItem[]>((acc, id) => {
        const item = inspectionItems.find(
          (it) => it.item_type === InspectionItemType.PolylineLength && it.inspection_item_id === id,
        )
        if (item) acc.push(item)
        return acc
      }, []),
    [inspectionItems, selectedElementIds],
  )

  const allWidths = useMemo(
    () =>
      // but we want the width items
      allItems.reduce<InspectionItem[]>((acc, polyline) => {
        const widthItem = inspectionItems.find(
          (item) =>
            item.item_type === InspectionItemType.PolylineWidth &&
            item.polyline_length_id === polyline.inspection_item_id,
        )
        if (widthItem) {
          acc.push(widthItem)
        }
        return acc
      }, []),
    [allItems, inspectionItems],
  )

  /**
   * Checks if there's any polyline selected.
   */
  const isPolylineSelected = useMemo(() => allItems.length > 0, [allItems])
  const isUpdating = useMemo(
    () => allItems.some((item) => updatingPolylines.includes(item.inspection_item_id!)),
    [allItems, updatingPolylines],
  )

  /**
   * Default width to display in input field.
   * - Polyline selected: width of first selected polyline
   */
  const defaultWidth = useMemo<number | undefined>(() => {
    if (allWidths.length > 0) {
      return allWidths.find((w) => w !== undefined)?.polyline_width?.estimated_value ?? undefined
    }

    return undefined
  }, [allWidths])

  /**
   * Save handler for updating existing polyline's width.
   * Creation will still be handled by `useEditor`.
   */
  const handleSubmit = useCallback(async () => {
    if (!project || !inspectionArea || !inspectionSheet) return

    const token = await getAccessToken()
    if (!token) return

    // need to separate widths to create and update
    // this is because if the width has not been set from the beginning,
    // there's no inspection item yet to update.
    const toCreate = allItems.filter(
      (item) =>
        allWidths.every((w) => w.polyline_length_id !== item.inspection_item_id) &&
        editedPolylines.some((ep) => ep.id === item.inspection_item_id && ep.width !== undefined),
    )
    const toUpdate = allWidths.filter((w) =>
      editedPolylines.some(
        (ep) =>
          ep.id === w.polyline_length_id && ep.width !== w.polyline_width?.estimated_value && ep.width !== undefined,
      ),
    )

    // Delete widths that are cleared by user (set to undefined)
    // Remember we need polyline_width's inspection item id, not the polyline_length
    const toDelete: string[] = []
    editedPolylines.forEach(({ id, width }) => {
      const originalWidthItem = allWidths.find((i) => i.polyline_length_id === id)

      if (
        originalWidthItem &&
        Number.isFinite(originalWidthItem.polyline_width?.estimated_value) &&
        width === undefined
      ) {
        toDelete.push(originalWidthItem.inspection_item_id!)
      }
    })

    try {
      const results = (
        await Promise.allSettled([
          toUpdate.length
            ? dispatch(
                updatePolylines({
                  accessToken: token,
                  projectId: project.project_id,
                  items: toUpdate.map((item) => {
                    const editedItem = editedPolylines.find((ep) => ep.id === item.polyline_length_id)
                    const editedWidth = editedItem ? (editedItem.width ?? 0) : item.polyline_width?.estimated_value

                    return {
                      ...item,
                      polyline_width: {
                        ...item.polyline_width,
                        estimated_value: editedWidth,
                      },
                    }
                  }),
                }),
              ).unwrap()
            : true,
          toCreate.length
            ? dispatch(
                addPolylineWidths({
                  accessToken: token,
                  projectId: project.project_id,
                  inspectionAreaId: inspectionArea.inspection_area_id,
                  inspectionSheetId: inspectionSheet.inspection_sheet_id,
                  items: toCreate.map((item) => ({
                    item_type: InspectionItemType.PolylineWidth,
                    polyline_width: {
                      estimated_value: editedPolylines.find((ep) => ep.id === item.inspection_item_id)?.width,
                    },
                    polyline_length_id: item.inspection_item_id!,
                  })),
                }),
              ).unwrap()
            : true,
          toDelete.length
            ? dispatch(
                deleteInspectionItems({
                  accessToken: token,
                  projectId: project.project_id,
                  areaId: inspectionArea.inspection_area_id,
                  sheetId: inspectionSheet.inspection_sheet_id,
                  items: toDelete,
                }),
              ).unwrap()
            : true,
        ])
      ).flat()

      // Only consider successful results for UI update, but still show modal if there's any failure.
      const successResults = results
        .filter(
          (res): res is { status: 'fulfilled'; value: InspectionItem[] } =>
            res.status === 'fulfilled' && Array.isArray(res.value),
        )
        .map((res) => res.value)
        .flat()

      dispatch(resetEditedPolylines())
      dispatch(upsertInspectionItems(successResults))
      dispatch(setWorkingWidth(undefined))

      // If any of the operations failed, show error modal. But still update the UI with successful ones.
      if (results.some((res) => res.status === 'rejected' || !res.value)) {
        showErrorModal(COMMON_MESSAGE)
      }
    } catch (error) {
      showErrorModal(COMMON_MESSAGE)
      Sentry.captureException(error)
    }
  }, [
    project,
    inspectionArea,
    inspectionSheet,
    editedPolylines,
    allItems,
    allWidths,
    dispatch,
    getAccessToken,
    showErrorModal,
  ])

  /**
   * Update width for all selected polylines in the editor as well as the working width.
   */
  const handleUpdateWidth = useCallback(
    (width?: number) => {
      if (allItems.length) {
        dispatch(
          upsertEditedPolylines(
            allItems.map((item) => ({
              id: item.inspection_item_id!,
              points:
                editedPolylines.find((ep) => ep.id === item.inspection_item_id)?.points ||
                item.polyline_length?.positions_for_distance ||
                [],
              width,
            })),
          ),
        )
      }

      if (selectedElementIds.length) {
        dispatch(updateCompletedPolylineWidths({ ids: selectedElementIds, width }))
      }

      dispatch(setWorkingWidth(width))
    },
    [allItems, selectedElementIds, editedPolylines, dispatch],
  )

  /**
   * Once a polyline (with width) is selected, set value to workingWidth.
   */
  useEffect(() => {
    // Unlike allItems which only has inspection items, we need to include
    // completed polylines as well for this
    const trueAllWidths = selectedElementIds.reduce<(number | undefined)[]>((acc, id) => {
      const item = inspectionItems.find(
        (it) => it.item_type === InspectionItemType.PolylineLength && it.inspection_item_id === id,
      )
      if (item) {
        // Look into edited values first. Always prefer edited value over original value
        // since that's what user is currently working with, even if it's undefined (meaning user cleared the width).
        const editedItem = editedPolylines.find((ep) => ep.id === item.inspection_item_id)
        if (editedItem) {
          acc.push(editedItem.width)
          return acc
        }

        // Fallback to original value if not edited
        const widthItem = inspectionItems.find(
          (it) =>
            it.item_type === InspectionItemType.PolylineWidth && it.polyline_length_id === item.inspection_item_id,
        )

        if (
          widthItem?.polyline_width?.estimated_value !== undefined &&
          widthItem?.polyline_width?.estimated_value !== null
        )
          acc.push(widthItem.polyline_width.estimated_value)
        return acc
      }

      // Lastly, check if it's from completed polylines
      const completed = completedPolylines.find((cp) => cp.id === id)
      if (completed?.width !== undefined) acc.push(completed.width)
      return acc
    }, [])

    if (trueAllWidths.length) {
      dispatch(setWorkingWidth(trueAllWidths.find((w) => w !== undefined)))
    }
  }, [inspectionItems, completedPolylines, editedPolylines, selectedElementIds, dispatch])

  /**
   * When tool is switched and no polyline is selected, clear the working width.
   */
  useEffect(() => {
    if (!isSelectedTool && !isPolylineSelected) {
      dispatch(setWorkingWidth(undefined))
    }
  }, [isSelectedTool, isPolylineSelected, dispatch])

  /**
   * If situation that requires this panel changes, we need to manually update the input field.
   * This is done manually due to use of `defaultValue` that wont update unless re-mount happens.
   * This can be a problem when a polyline is selected, filled with a default of that polyline,
   * switched over to the tool which is suppose to clear the field but updating `defaultWidth`
   * is not going to do anything.
   */
  useEffect(() => {
    if (!inputRef.current) return

    // empty on tool switch and no widths
    if (workingWidth !== undefined) {
      inputRef.current.value = String(workingWidth)
    } else {
      inputRef.current.value = ''
    }
  }, [workingWidth])

  if (!(isSelectedTool || (isPolylineSelected && selectedTool === EDITOR_TOOLS.MOVE))) return null

  return (
    <>
      <CollapsePanel
        title="延長計算の設定"
        // title="延長の幅"
        onChange={() => dispatch(toggleCollapsedPanel(PanelType.Tool))}
        isCollapsed={isCollapsed}
      >
        <Box data-testid="polyline-width-panel" className="inner-container">
          {isSelectedTool && userProfile?.is_beta_user && (
            <Field.Root px={INFO_PANEL_PADDING} py={INFO_PANEL_PADDING} alignItems="stretch">
              <Field.Label color="white">自動検出</Field.Label>
              {isCrackDetectionDirty && isDetectingCrack && isPolylineMode ? (
                <Alert.Root status="info" variant="panel-info" mb={4} p={3} gap={0.5}>
                  <HStack align="start">
                    <Icon as={IoInformationCircleOutline} boxSize="2em" />
                    <Text fontSize="sm">{detectionLabel}を実行中です。完了までしばらくお待ちください。</Text>
                  </HStack>
                  <HStack w="100%" justifyContent="end">
                    <Button
                      variant="outline"
                      borderColor="whiteAlpha.400"
                      color="red.500"
                      size="xs"
                      fontSize="sm"
                      mt={4}
                      onClick={() => {
                        showModal({
                          title: `${detectionLabel}のリセット`,
                          body: `本当に${detectionLabel}をリセットしますか？進行中の検出結果は保存されません。`,
                          modalType: MODAL_TYPES.CONFIRMATION_CRITICAL,
                          confirmText: 'リセット',
                          onConfirm: () => {
                            dispatch(resetCurrent())
                            return true
                          },
                        })
                      }}
                    >
                      リセット
                    </Button>
                  </HStack>
                </Alert.Root>
              ) : null}

              <VStack align="stretch" gap={2} display={mode === PolylineToolMode.Draw ? 'flex' : 'none'}>
                {DETECTION_BUTTONS.map(({ kind, label, loadingText }) => {
                  // Only the kind actually running spins — otherwise a チョーキング run would also
                  // put クラック検出中... on the button beside it.
                  const isRunningThisKind = (isSavingDetectedPolyline || isDetectingCrack) && kind === detectionKind

                  return (
                    <Tooltip
                      key={label}
                      label={!isMeshPresent ? <Text color="orange">3Dメッシュが必要です</Text> : undefined}
                      showArrow
                      positioning={{ placement: 'top' }}
                    >
                      <Button
                        variant="panel"
                        disabled={
                          polylineWorkingPoints.length > 0 ||
                          !isMeshPresent ||
                          isSavingDetectedPolyline ||
                          isDetectingCrack
                        }
                        loading={isRunningThisKind}
                        loadingText={isSavingDetectedPolyline ? '延長保存中...' : loadingText}
                        onClick={() => {
                          // `initWorking` decides the stage — a run that failed keeps its polygon
                          // and comes back at `Preview`, so forcing `Drawing` here would ask the
                          // user for another vertex on an outline they already closed.
                          dispatch(initWorking(kind))
                          dispatch(setMode(PolylineToolMode.CrackDetection))
                        }}
                      >
                        {label}
                      </Button>
                    </Tooltip>
                  )
                })}
              </VStack>

              {isChalkDetection && mode === PolylineToolMode.CrackDetection ? (
                <HStack justify="space-between" mt={2} mb={2}>
                  <Text color="gray2.400" fontSize="sm">
                    選択した色（目安）
                  </Text>
                  {pointForTargetColor && targetColorHex ? (
                    <HStack gap={2}>
                      <Box
                        data-testid="polyline-target-color-swatch"
                        boxSize="20px"
                        borderRadius="sm"
                        borderWidth="1px"
                        borderColor="whiteAlpha.400"
                        background={targetColorHex}
                      />
                      <Text color="gray2.300" fontSize="sm" fontFamily="mono">
                        {targetColorHex}
                      </Text>
                    </HStack>
                  ) : (
                    <Text color="gray2.500" fontSize="sm">
                      {pointForTargetColor ? '取得できませんでした' : '未選択'}
                    </Text>
                  )}
                </HStack>
              ) : null}

              <Button
                w="100%"
                variant="panel-dark-outline"
                display={mode === PolylineToolMode.CrackDetection ? 'inline-flex' : 'none'}
                disabled={isDetectingCrack}
                borderColor="red.500"
                onClick={() => {
                  dispatch(cancelWorking())
                  dispatch(setMode(PolylineToolMode.Draw))
                  dispatch(restoreBackupExecution())
                }}
              >
                <Icon as={CancelIcon} color="red.500" boxSize={5} />
                キャンセル
              </Button>
            </Field.Root>
          )}

          <Field.Root
            px={INFO_PANEL_PADDING}
            py={INFO_PANEL_PADDING}
            display={mode === PolylineToolMode.Draw ? 'block' : 'none'}
          >
            <Field.Label color="white">幅</Field.Label>
            <Tooltip
              label={
                isWidthLocked ? (
                  <Text color="orange">クラック検出を使用しているため、幅を変更できません。</Text>
                ) : undefined
              }
              showArrow
              positioning={{ placement: 'top' }}
            >
              <InputGroup endElement={<Span color="gray2.300">mm</Span>}>
                <Input
                  ref={inputRef}
                  type="number"
                  defaultValue={defaultWidth}
                  size="sm"
                  background="gray2.700"
                  borderColor="whiteAlpha.200"
                  borderRadius={5}
                  disabled={isWidthLocked}
                  onChange={(e) => {
                    const val = e.target.valueAsNumber
                    if (Number.isNaN(val) || val < 0) {
                      handleUpdateWidth(undefined)
                    } else {
                      handleUpdateWidth(val)
                    }
                  }}
                />
              </InputGroup>
            </Tooltip>
            {selectedElementIds.length > 1 ? (
              <Alert.Root variant="panel-info" mt={2}>
                複数の延長を選択すると、選択したすべての延長の幅が更新されます。
              </Alert.Root>
            ) : null}
          </Field.Root>

          {isSelectedTool && (
            <Field.Root px={INFO_PANEL_PADDING} py={INFO_PANEL_PADDING}>
              <Field.Label color="white" fontSize="sm" justifyContent="space-between">
                ラベル表示（編集中）
              </Field.Label>
              <VStack align="stretch" gap={2} w="full" pl={4}>
                <HStack justify="space-between">
                  <Text color="gray2.400" fontSize="sm">
                    全長
                  </Text>
                  <Switch.Root
                    colorPalette="green"
                    checked={inToolLabelVisibility.primary}
                    onCheckedChange={(e) => {
                      dispatch(setInToolLabelVisibility({ primary: e.checked }))
                    }}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                  </Switch.Root>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray2.400" fontSize="sm">
                    各辺
                  </Text>
                  <Switch.Root
                    colorPalette="green"
                    checked={inToolLabelVisibility.edge}
                    onCheckedChange={(e) => {
                      dispatch(setInToolLabelVisibility({ edge: e.checked }))
                    }}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                  </Switch.Root>
                </HStack>
              </VStack>
            </Field.Root>
          )}
        </Box>
      </CollapsePanel>

      {isPolylineSelected && !isSelectedTool && (
        <VStack className="tool-buttons">
          <Box w="100%">
            <Button
              colorPalette="blue"
              size={isTablet ? 'lg' : 'md'}
              fontSize="md"
              onClick={handleSubmit}
              loading={isUpdating}
              disabled={editedPolylines.length === 0 || isUpdating}
              spinnerPlacement="end"
              loadingText="保存中"
              width="full"
              justifyContent="space-between"
            >
              <CheckCircleIcon />
              保存
            </Button>
          </Box>
        </VStack>
      )}
    </>
  )
}

export default ToolSettings
